import { inject, Injectable, INJECTOR, resource } from '@angular/core';
import { SOFTLINE_MDE_INVENTORY_ITEM_REPOSITORY } from '../repositories/inventory-item.repository';
import { InventoryService } from './inventory.service';
import { InventoryItem } from '../types/inventory-item';
import { handleRequestError } from '@softline/application';
import { MessageBarService } from '@softline/ui-core';

@Injectable()
export class InventoryItemService {
  readonly #repository = inject(SOFTLINE_MDE_INVENTORY_ITEM_REPOSITORY);
  readonly #inventoryService = inject(InventoryService);
  readonly #messageBarService = inject(MessageBarService);
  readonly #injector = inject(INJECTOR);

  // reload items whenever the inventory changes
  readonly #resource = resource({
    request: () => this.#inventoryService.value() ?? undefined,
    loader: async () => {
      return this.#repository.load();
    },
  });

  readonly resource = this.#resource.asReadonly();

  async create(item: Partial<InventoryItem & { lagerbean?: any }>): Promise<void> {
    try {
      const newItem = await this.#repository.add(item);
      const existingItems = this.#resource.value() ?? [];

      const existItem = existingItems.find(
        (existingItem) =>
          existingItem.artikel.id === newItem.artikel?.id &&
          existingItem.einheit.id === newItem.einheit?.id
      );

      if (!existItem) {
        this.#messageBarService.success('Neue Position wurde erfolgreich erstellt!');
        this.#resource.set([...existingItems, newItem]);
        return;
      }

      this.#messageBarService.success('Die Menge des Artikels wurde erhöht!');

      const newExistingItems = existingItems.filter(
        (existingItem) =>
          existingItem.artikel.id !== newItem.artikel?.id &&
          existingItem.einheit.id !== newItem.einheit?.id
      );

      this.#resource.set([...newExistingItems, newItem]);
    } catch (e) {
      handleRequestError(e, this.#injector, { show: true });
    }
  }

  async update(id: number, changes: Partial<InventoryItem>): Promise<void> {
    try {
      const items = [...(this.#resource.value() ?? [])];
      const existingItemIndex = items.findIndex((item) => item.id === id);
      if (existingItemIndex === -1) return;

      const newItem = await this.#repository.update({
        ...items[existingItemIndex],
        ...changes,
      });

      items[existingItemIndex] = newItem;

      this.#resource.set(items);
      this.#messageBarService.success(
        'Bestehende Position wurde erfolgreich aktualisiert!'
      );
    } catch (e) {
      handleRequestError(e, this.#injector, { show: true });
    }
  }

  async remove(id: number): Promise<void> {
    try {
      await this.#repository.remove(id);
      this.#resource.set((this.resource.value() ?? []).filter((item) => item.id !== id));
      this.#messageBarService.success('Löschen der Position erfolgreich!');
    } catch (e) {
      handleRequestError(e, this.#injector, { show: true });
    }
  }
}
